import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../api/emergencyAccessApi'
import {
  EmergencyError,
  createGrantFlow,
  initEmergencyKeypairFlow,
  rewrapGrantFlow,
} from '../services/emergencyAccessFlow'
import type { EmergencyAccessStatus, GrantListItemWire, ReceivedListItemWire } from '../types'

/**
 * Track F: Emergency Access UI hook (`pages/settings/EmergencySection.tsx` 用)。
 *
 * 役割:
 *   - 起動時に GET /grants + GET /received で両側の状態を取得
 *   - 各 action (invite / accept / decline / request / cancel / approve / deny /
 *     revoke / unlock) をラップして state 機械を回す
 *   - エラーは messageKey に変換して UI 側で `t()` する (CLAUDE.md §2 ルール 10)
 */

export type EmergencyState =
  | { kind: 'loading' }
  | { kind: 'ready'; grants: GrantListItemWire[]; received: ReceivedListItemWire[] }
  | { kind: 'error'; messageKey: string }

const MESSAGE_KEYS: Record<EmergencyError['code'], string> = {
  no_session: 'settings.emergency.error.no_session',
  already_initialized: 'settings.emergency.error.already_initialized',
  self_invite: 'settings.emergency.error.self_invite',
  duplicate_invite: 'settings.emergency.error.duplicate_invite',
  not_found: 'settings.emergency.error.not_found',
  bad_request: 'settings.emergency.error.bad_request',
  forbidden: 'settings.emergency.error.forbidden',
  too_many_requests: 'settings.emergency.error.too_many_requests',
  network: 'settings.emergency.error.network',
  generic: 'settings.emergency.error.generic',
}

const messageKeyFor = (err: unknown): string => {
  if (err instanceof EmergencyError) return MESSAGE_KEYS[err.code]

  return MESSAGE_KEYS.generic
}

/**
 * RSA keypair 生成は重い (~500ms)。アプリ起動後に 1 回だけ init を試行し、
 * EmergencySection の再 mount ごとの再 keygen を防ぐためのモジュール level フラグ。
 */
let keypairInitAttempted = false

export interface UseEmergencyAccess {
  state: EmergencyState
  lastErrorKey: string | null
  refresh: () => Promise<void>
  /** Init RSA keypair on first action (idempotent, skips if already initialized) */
  initKeypair: () => Promise<void>
  invite: (granteeEmail: string, waitDays: number, accessType: 'view' | 'takeover') => Promise<void>
  accept: (id: string) => Promise<void>
  decline: (id: string) => Promise<void>
  request: (id: string) => Promise<void>
  cancelRequest: (id: string) => Promise<void>
  approve: (id: string) => Promise<void>
  deny: (id: string) => Promise<void>
  revoke: (id: string) => Promise<void>
  /** 既存 invite の設定 (待機日数 / アクセス種別) を編集する。 */
  editGrant: (id: string, waitDays: number, accessType: 'view' | 'takeover') => Promise<void>
  /** 履歴 (REVOKED / DECLINED) の行を物理削除する。 */
  deleteEntry: (id: string) => Promise<void>
  /** placeholder で wrappedSk=NULL のまま残った grant を grantee の現 pubkey で再 wrap する (L-24)。 */
  rewrap: (id: string, granteePubKey: string) => Promise<void>
}

export const useEmergencyAccess = (): UseEmergencyAccess => {
  const [state, setState] = useState<EmergencyState>({ kind: 'loading' })
  const [lastErrorKey, setLastErrorKey] = useState<string | null>(null)

  const refresh = useCallback(async (): Promise<void> => {
    setLastErrorKey(null)
    try {
      const [grants, received] = await Promise.all([api.listGrants(), api.listReceived()])

      setState({ kind: 'ready', grants, received })
    } catch (err: unknown) {
      const key = messageKeyFor(err)

      setState({ kind: 'error', messageKey: key })
      setLastErrorKey(key)
    }
  }, [])

  /**
   * Wraps any mutating action: runs it, captures error → messageKey, refreshes
   * the list. Used internally for accept / decline / request / cancel / approve /
   * deny / revoke.
   *
   * `inFlightRef` で多重実行を弾く — ボタン連打で同じ遷移が 2 回走り、
   * 通知が重複する (例: 解錠要求の二重通知) のを防ぐ。
   */
  const inFlightRef = useRef(false)

  const runAction = useCallback(
    async (fn: () => Promise<void>): Promise<void> => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      setLastErrorKey(null)
      try {
        await fn()
      } catch (err: unknown) {
        setLastErrorKey(messageKeyFor(err))

        return
      } finally {
        inFlightRef.current = false
      }
      await refresh()
    },
    [refresh],
  )

  const initKeypair = useCallback(async (): Promise<void> => {
    // アプリ起動後 1 回だけ試行する (mount のたびの再 keygen を防ぐ)。
    if (keypairInitAttempted) return
    keypairInitAttempted = true
    setLastErrorKey(null)
    try {
      await initEmergencyKeypairFlow()
      // 新規 init 成功 — server 側で自分宛 placeholder invite が backfill される。
      // mount 時の refresh は init より前に走っているため、ここで再取得する。
      await refresh()
    } catch (err: unknown) {
      if (err instanceof EmergencyError && err.code === 'already_initialized') {
        // benign: another tab / earlier run already provisioned. Continue.
        return
      }
      // network 等の一時失敗は再試行を許す。
      keypairInitAttempted = false
      setLastErrorKey(messageKeyFor(err))
    }
  }, [refresh])

  const invite = useCallback(
    async (
      granteeEmail: string,
      waitDays: number,
      accessType: 'view' | 'takeover',
    ): Promise<void> => {
      setLastErrorKey(null)
      // ensure my emergencyPubKey exists before granter wraps (granter side doesn't
      // need their own pubkey, but if user is also a grantee they will. Cheap init.)
      try {
        await createGrantFlow(granteeEmail, waitDays, accessType)
      } catch (err: unknown) {
        setLastErrorKey(messageKeyFor(err))

        return
      }
      await refresh()
    },
    [refresh],
  )

  const accept = useCallback(
    async (id: string) => runAction(() => api.acceptReceived(id).then(() => undefined)),
    [runAction],
  )

  const decline = useCallback(
    async (id: string) => runAction(() => api.declineReceived(id).then(() => undefined)),
    [runAction],
  )

  const request = useCallback(
    async (id: string) => runAction(() => api.requestReceived(id).then(() => undefined)),
    [runAction],
  )

  const cancelRequest = useCallback(
    async (id: string) => runAction(() => api.cancelReceivedRequest(id).then(() => undefined)),
    [runAction],
  )

  const approve = useCallback(
    async (id: string) => runAction(() => api.approveGrant(id).then(() => undefined)),
    [runAction],
  )

  const deny = useCallback(
    async (id: string) => runAction(() => api.denyGrant(id).then(() => undefined)),
    [runAction],
  )

  const revoke = useCallback(
    async (id: string) => runAction(() => api.revokeGrant(id).then(() => undefined)),
    [runAction],
  )

  const editGrant = useCallback(
    async (id: string, waitDays: number, accessType: 'view' | 'takeover') =>
      runAction(() => api.updateGrant(id, { waitDays, accessType }).then(() => undefined)),
    [runAction],
  )

  const deleteEntry = useCallback(
    async (id: string) => runAction(() => api.deleteEmergencyHistory(id).then(() => undefined)),
    [runAction],
  )

  const rewrap = useCallback(
    async (id: string, granteePubKey: string) =>
      runAction(() => rewrapGrantFlow(id, granteePubKey)),
    [runAction],
  )

  useEffect(() => {
    void refresh()
  }, [refresh])

  return {
    state,
    lastErrorKey,
    refresh,
    initKeypair,
    invite,
    accept,
    decline,
    request,
    cancelRequest,
    approve,
    deny,
    revoke,
    editGrant,
    deleteEntry,
    rewrap,
  }
}

/** Re-export for UI badge color mapping */
export type { EmergencyAccessStatus }
